<template>
  <!-- bootstrap-vue 2.23 b-form-group'un kullanılan alt kümesiyle aynı DOM (Aşama 2.3):
       label-for yoksa fieldset > legend.bv-no-focus-ring.col-form-label.pt-0 + div,
       varsa div[role=group].form-group > label.d-block + div; label-cols-* / content-cols-* verilince
       yatay düzen (form-row + col-* sütunları), description → small.form-text.text-muted. -->
  <fieldset v-if="isFieldset" :id="groupId" class="form-group" :disabled="disabled"
            :aria-labelledby="isHorizontal ? labelId : null">
    <div v-if="isHorizontal" class="form-row">
      <legend tabindex="-1" :id="labelId" :class="labelClasses" @click="onLegendClick"><slot name="label">{{ label }}</slot></legend>
      <div ref="content" :class="contentClasses">
        <slot :ariaDescribedby="ariaDescribedby" :descriptionId="descriptionId" :id="groupId" :labelId="labelId" />
        <small v-if="hasDescription()" :id="descriptionId" tabindex="-1" class="form-text text-muted"><slot name="description">{{ description }}</slot></small>
      </div>
    </div>
    <template v-else>
      <div v-if="hint && hasLabel()" class="est-label-row">
        <legend tabindex="-1" :id="labelId" :class="labelClasses" @click="onLegendClick"><slot name="label">{{ label }}</slot></legend>
        <field-hint :text="hint" :label="label" />
      </div>
      <legend v-else-if="hasLabel()" tabindex="-1" :id="labelId" :class="labelClasses" @click="onLegendClick"><slot name="label">{{ label }}</slot></legend>
      <div ref="content">
        <slot :ariaDescribedby="ariaDescribedby" :descriptionId="descriptionId" :id="groupId" :labelId="labelId" />
        <small v-if="hasDescription()" :id="descriptionId" tabindex="-1" class="form-text text-muted"><slot name="description">{{ description }}</slot></small>
      </div>
    </template>
  </fieldset>
  <div v-else :id="groupId" role="group" class="form-group" :class="{ 'form-row': isHorizontal }">
    <div v-if="hint && hasLabel() && !isHorizontal" class="est-label-row">
      <label :id="labelId" :for="labelFor" :class="labelClasses"><slot name="label">{{ label }}</slot></label>
      <field-hint :text="hint" :label="label" />
    </div>
    <label v-else-if="hasLabel() || isHorizontal" :id="labelId" :for="labelFor" :class="labelClasses"><slot name="label">{{ label }}</slot></label>
    <div ref="content" :class="contentClasses">
      <slot :ariaDescribedby="ariaDescribedby" :descriptionId="descriptionId" :id="groupId" :labelId="labelId" />
      <small v-if="hasDescription()" :id="descriptionId" tabindex="-1" class="form-text text-muted"><slot name="description">{{ description }}</slot></small>
    </div>
  </div>
</template>

<script>
  import FieldHint from './FieldHint.vue';

  const BREAKPOINTS = ['', 'sm', 'md', 'lg', 'xl'];
  const LOGICAL_ALIGN = { left: 'start', right: 'end' };
  const suffix = (breakpoint, name) => (breakpoint ? name + breakpoint.charAt(0).toUpperCase() + breakpoint.slice(1) : name);
  const INPUT_SELECTOR = ['input', 'select', 'textarea'].map(tag => `${tag}:not([disabled])`).join();
  const LEGEND_INTERACTIVE_ELEMENTS = ['INPUT', 'SELECT', 'TEXTAREA', 'A', 'BUTTON', 'LABEL'];

  let nextId = 0;

  const breakpointProps = BREAKPOINTS.reduce((props, breakpoint) => {
    props[suffix(breakpoint, 'labelCols')] = { type: [Boolean, Number, String], default: undefined };
    props[suffix(breakpoint, 'contentCols')] = { type: [Boolean, Number, String], default: undefined };
    props[suffix(breakpoint, 'labelAlign')] = { type: String, default: undefined };
    return props;
  }, {});

  export default {
    name: 'FormGroup',
    components: { FieldHint },
    props: {
      id: { type: String, default: undefined },
      label: { type: String, default: undefined },
      labelFor: { type: String, default: undefined },
      labelSize: { type: String, default: undefined },
      labelClass: { type: [String, Array, Object], default: undefined },
      description: { type: String, default: undefined },
      // Alan açıklaması: etiketin yanında "?" düğmesi ve ipucu (FieldHint). Verilmezse DOM bootstrap-vue ile aynı kalır.
      hint: { type: String, default: undefined },
      disabled: { type: Boolean, default: false },
      ...breakpointProps
    },
    data() {
      nextId += 1;
      return { localId: `form-group-${nextId}` };
    },
    computed: {
      groupId() {
        return this.id || this.localId;
      },
      labelId() {
        return this.hasLabel() ? `${this.groupId}__label` : null;
      },
      descriptionId() {
        return this.hasDescription() ? `${this.groupId}__description` : null;
      },
      ariaDescribedby() {
        return this.descriptionId || null;
      },
      isFieldset() {
        return !this.labelFor;
      },
      labelColClasses() {
        return this.colClasses('labelCols');
      },
      contentColClasses() {
        return this.colClasses('contentCols');
      },
      isHorizontal() {
        return this.labelColClasses.length > 0 || this.contentColClasses.length > 0;
      },
      labelClasses() {
        const horizontal = this.isHorizontal;
        const fieldset = this.isFieldset;
        const align = BREAKPOINTS
          .map(breakpoint => {
            const value = this[suffix(breakpoint, 'labelAlign')];
            // BootstrapVue değerleri (left/right) BS5 mantıksal sınıflarına (start/end) çevrilir.
            return value ? ['text', breakpoint, LOGICAL_ALIGN[value] || value].filter(Boolean).join('-') : null;
          })
          .filter(Boolean);
        return [
          horizontal ? (this.labelColClasses.length ? this.labelColClasses : ['col']) : [],
          fieldset ? 'bv-no-focus-ring' : '',
          horizontal || fieldset ? 'col-form-label' : '',
          !horizontal && fieldset ? 'pt-0' : '',
          !horizontal && !fieldset ? 'd-block' : '',
          this.labelSize ? `col-form-label-${this.labelSize}` : '',
          align,
          this.labelClass
        ];
      },
      contentClasses() {
        if (!this.isHorizontal) return null;
        return this.contentColClasses.length ? this.contentColClasses : ['col'];
      }
    },
    watch: {
      ariaDescribedby(newValue, oldValue) {
        if (newValue !== oldValue) this.updateAriaDescribedby(newValue, oldValue);
      }
    },
    mounted() {
      this.$nextTick(() => this.updateAriaDescribedby(this.ariaDescribedby));
    },
    methods: {
      hasLabel() {
        return !!(this.$slots.label || this.label);
      },
      hasDescription() {
        return !!(this.$slots.description || this.description);
      },
      // bootstrap-vue getColProps: '' / true → eşit genişlik (col, col-md), sayı → col-{bp}-{n}, 'auto'.
      colClasses(prefix) {
        return BREAKPOINTS.reduce((classes, breakpoint) => {
          let value = this[suffix(breakpoint, prefix)];
          value = value === '' ? true : value || false;
          if (typeof value !== 'boolean' && value !== 'auto') {
            value = parseInt(value, 10);
            value = value > 0 ? value : false;
          }
          if (value) {
            classes.push(value === true ? ['col', breakpoint].filter(Boolean).join('-') : ['col', breakpoint, value].filter(Boolean).join('-'));
          }
          return classes;
        }, []);
      },
      // label-for'daki alana description kimliğini ekler (bootstrap-vue updateAriaDescribedby).
      updateAriaDescribedby(newValue, oldValue) {
        if (!this.labelFor || !this.$refs.content) return;
        const input = this.$refs.content.querySelector(`#${CSS.escape(this.labelFor)}`);
        if (!input) return;
        const newIds = (newValue || '').split(/\s+/);
        const oldIds = (oldValue || '').split(/\s+/);
        const ids = (input.getAttribute('aria-describedby') || '')
          .split(/\s+/)
          .filter(id => oldIds.indexOf(id) === -1)
          .concat(newIds)
          .filter((id, index, all) => id && all.indexOf(id) === index)
          .join(' ');
        if (ids) input.setAttribute('aria-describedby', ids);
        else input.removeAttribute('aria-describedby');
      },
      // legend label gibi davranır: tek görünür alan varsa ona odaklanır.
      onLegendClick(event) {
        const tagName = event.target ? event.target.tagName : '';
        if (LEGEND_INTERACTIVE_ELEMENTS.indexOf(tagName) !== -1 || !this.$refs.content) return;
        const inputs = Array.prototype.filter.call(
          this.$refs.content.querySelectorAll(INPUT_SELECTOR),
          el => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)
        );
        if (inputs.length === 1 && typeof inputs[0].focus === 'function') inputs[0].focus();
      }
    }
  };
</script>
